/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */
import { ref } from "vue";
import { defineStore } from "pinia";
import { api } from "jiffy-api";
import { mergeImages } from "components/jiffy/vue-product-details-dtf/utils";
import { sleep } from "common/utils/helpers/delay";
// eslint-disable-next-line import/no-cycle
import { useDtfStore } from "./store";

const STICKER_IMAGES_CACHE_NAME = "sticker-images-cache";
const MAX_CACHE_ITEMS = 240;

type CutlineData = Record<string, Record<string, string>>;

type CutlinesMap = Record<number, Record<string, CutlineData>>;

type StickerArtwork = {
  id: number;
  shape: string;
  paddingSize: string;
  bgColor: string;
  removeBackground: boolean;
  url: string;
  stickerCutlineNoBgUrl: string;
  originalNoBgUrl: string;
  stickerCutlineWithBgUrl: string;
  originalImageUrl: string;
  processingImage?: boolean;
};

function retry(fn: () => Promise<unknown>, retries = 10, interval = 1000) {
  const perform = async (retryCount: number, retryInterval: number) => {
    try {
      return await fn();
    } catch (error) {
      if (retryCount <= 0) throw error;
      await sleep(retryInterval);
      return perform(retryCount - 1, retryInterval * 2);
    }
  };

  return perform(retries, interval);
}

async function manageCacheSize() {
  const cache = await caches.open(STICKER_IMAGES_CACHE_NAME);
  const keys = await cache.keys();

  if (keys.length > MAX_CACHE_ITEMS && keys[0]) {
    await cache.delete(keys[0]);
    await manageCacheSize();
  }
}

function extractUrlsByBgStyleAndPadding(cutlineData: Record<string, Record<string, string>>) {
  const urls: Record<string, string> = {};
  Object.entries(cutlineData).forEach(([bgStyle, sizes]) => {
    Object.entries(sizes).forEach(([paddingSize, url]) => {
      if (url) {
        const key = `${bgStyle}-${paddingSize}`;
        urls[key] = url;
      }
    });
  });
  return urls;
}

async function cacheCutlineImages(
  data: { artwork: StickerArtwork; shape: string },
  cutlineData: CutlineData
) {
  const { artwork, shape } = data;

  const cache = await caches.open(STICKER_IMAGES_CACHE_NAME);
  const cacheKey = `${artwork.id}-${shape}`;

  const urls = extractUrlsByBgStyleAndPadding(cutlineData);

  await Promise.all(
    Object.entries(urls).map(async ([key, url]) => {
      const response = await fetch(url);
      if (response.ok) {
        await cache.put(`${cacheKey}-${key}`, response.clone());
      }
    })
  );

  await manageCacheSize();
}

async function getCachedCutlineImage(artwork: StickerArtwork, shape: string, paddingSize: string) {
  const { id: artworkId, removeBackground } = artwork;
  const bgStyle = removeBackground ? "no_bg" : "with_bg";

  const cacheKey = `${artworkId}-${shape}-${bgStyle}-${paddingSize}`;
  const cache = await caches.open(STICKER_IMAGES_CACHE_NAME);

  const cachedResponse = await cache.match(cacheKey);

  if (cachedResponse) return cachedResponse;

  throw new Error(`Image not found in cache for: ${cacheKey}`);
}

export const useStickersStore = defineStore("stickers", () => {
  const dtfStore = useDtfStore();

  const isLoading = ref(false);
  const cutlines = ref<CutlinesMap>({});

  const setStickerCutlines = ({
    artwork,
    shape,
    cutlines: cutlinesData
  }: {
    artwork: StickerArtwork;
    shape: string;
    cutlines: CutlineData;
  }) => {
    const { id: artworkId } = artwork;
    if (!cutlines.value[artworkId]) {
      cutlines.value[artworkId] = {};
    }
    cutlines.value[artworkId][shape] = cutlinesData;
  };

  const setIsLoading = (newValue: boolean) => {
    isLoading.value = newValue;
  };

  const fetchCutlineUrl = async ({
    artwork,
    shape,
    paddingSize,
    removeBackground,
    useCache = true
  }: {
    artwork: StickerArtwork;
    shape: string;
    paddingSize: string;
    removeBackground: boolean;
    useCache?: boolean;
  }) => {
    const bgStyle = removeBackground ? "no_bg" : "with_bg";

    const artworkCutlines = cutlines.value[artwork.id]?.[shape];

    if (useCache && artworkCutlines && artworkCutlines[bgStyle]?.[paddingSize]) {
      try {
        // Check if the cutline URL is cached
        await getCachedCutlineImage(artwork, shape, paddingSize);
        return artworkCutlines[bgStyle][paddingSize];
      } catch (_error) {
        console.log(
          `Cache miss for artwork ${artwork.id}, ${shape}, ${bgStyle}, ${paddingSize}, refetching...`
        );
      }
    }

    if (artwork.shape !== shape && artworkCutlines) return null;

    const cutlinesData = (await retry(() =>
      api.dtf.stickerCutlines({ id: artwork.id })
    )) as CutlineData;

    await cacheCutlineImages({ artwork, shape }, cutlinesData);

    setStickerCutlines({ artwork, shape, cutlines: cutlinesData });

    return cutlinesData?.[bgStyle]?.[paddingSize];
  };

  // eslint-disable-next-line max-lines-per-function
  const generateStickerImage = async ({
    artwork,
    shape = artwork.shape,
    paddingSize = artwork.paddingSize,
    bgColor = artwork.bgColor,
    removeBackground = artwork.removeBackground,
    useCache = true
  }: {
    artwork: StickerArtwork;
    shape?: string;
    paddingSize?: string;
    bgColor?: string;
    removeBackground?: boolean;
    useCache?: boolean;
  }) => {
    try {
      setIsLoading(true);
      dtfStore.toggleLoading(["removeBg", true]);
      dtfStore.updateArtworkValuesById({
        id: artwork.id,
        url: artwork.url,
        processingImage: true
      });

      const cutlineUrl = await fetchCutlineUrl({
        artwork,
        shape,
        paddingSize,
        removeBackground,
        useCache
      });

      const urls = removeBackground
        ? [artwork.stickerCutlineNoBgUrl, artwork.originalNoBgUrl]
        : [artwork.stickerCutlineWithBgUrl, artwork.originalImageUrl];

      if (cutlineUrl) urls[0] = cutlineUrl;

      const previewUrl = await mergeImages({ urls, bgColor });

      dtfStore.updateArtworkValuesById({ id: artwork.id, url: previewUrl });
    } finally {
      setIsLoading(false);
      dtfStore.toggleLoading(["removeBg", false]);
      dtfStore.updateArtworkValuesById({
        id: artwork.id,
        processingImage: false
      });
    }
  };

  return {
    isLoading,
    cutlines,
    setStickerCutlines,
    setIsLoading,
    fetchCutlineUrl,
    generateStickerImage
  };
});
